import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth'

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const location = useLocation()
  const [user, setUser] = useState(null)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const userMenuRef = useRef(null)

  useEffect(() => {
    const auth = getAuth()
    const unsub = onAuthStateChanged(auth, (u) => setUser(u))
    return () => unsub()
  }, [])

  // Handle navigation click - close mobile menu and scroll to top
  const handleNavClick = (e, href) => {
    setIsMenuOpen(false)
    // Scroll to top of page smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Close user menu on outside click
  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (!isUserMenuOpen) return
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleDocumentClick)
    document.addEventListener('touchstart', handleDocumentClick)
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick)
      document.removeEventListener('touchstart', handleDocumentClick)
    }
  }, [isUserMenuOpen])

  const handleLogout = async () => {
    const auth = getAuth()
    try {
      await signOut(auth)
      setIsUserMenuOpen(false)
      setIsMenuOpen(false)
    } catch { }
  }

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Places', href: '/places' },
    { name: 'Events', href: '/events' },
    { name: 'Projects', href: '/projects' },
    { name: 'Contact', href: '/contact' },
  ]

  const isActive = (path) => location.pathname === path

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/20 backdrop-blur-md border-b border-secondary/5">
      <div className="container-width section-padding">
        <div className="relative flex items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img
              src="/images/logo.png"
              alt="Tamar Valley Business Association"
              className="h-12 w-auto"
              loading="eager"
              decoding="sync"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </Link>

          {/* Centered Navigation */}
          <nav className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 hidden md:flex space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className={`text-xs tracking-[0.3em] text-secondary/60 hover:text-secondary transition-all duration-500 uppercase  ${isActive(item.href)
                    ? 'text-secondary '
                    : 'text-foreground hover:text-secondary'
                  }`}
              >
                {item.name}
              </Link>
            ))}
            {user && (
              <Link to="/admin/feedback" className="text-xs tracking-[0.3em] text-secondary hover:text-white transition-colors uppercase font-bold">
                Admin
              </Link>
            )}
          </nav>

          {/* Right Section: Greeting, Social, Mobile Menu */}
          <div className="flex items-center space-x-4 ml-auto relative">
            {/* Greeting if logged in */}
            {user && (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(v => !v)}
                  className="text-sm text-foreground font-medium hover:text-secondary max-w-[200px] truncate"
                  aria-haspopup="menu"
                  aria-expanded={isUserMenuOpen}
                >
                  Hello, {user.email}
                </button>
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-36 rounded-md shadow-lg py-1 z-50 bg-white text-black">
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-3 py-2 text-sm text-red-500 hover:bg-gray-100"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            )}
            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-md text-foreground hover:text-secondary transition-colors duration-200"
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden pt-4 pb-4">
            <nav className="flex flex-col space-y-3">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className={`block text-xs tracking-[0.3em] text-secondary/60 hover:text-secondary transition-all duration-500 uppercase py-2 ${isActive(item.href)
                      ? 'text-white hover:text-secondary'
                      : 'text-white hover:text-secondary'
                    }`}
                >
                  {item.name}
                </Link>
              ))}
              {user && (
                <>
                  <Link
                    to="/admin/feedback"
                    onClick={(e) => handleNavClick(e, '/admin/feedback')}
                    className="block text-xs tracking-[0.3em] text-secondary hover:text-white transition-all duration-500 uppercase py-2 font-bold"
                  >
                    Admin Feedback
                  </Link>
                  <div className="px-3 py-2 rounded-md text-gray-300">
                    <div className="mb-2 max-w-[250px] truncate">Hello, {user.email}</div>
                    <button
                      onClick={handleLogout}
                      className="text-sm text-red-400 hover:bg-gray-800 px-3 py-2 rounded"
                    >
                      Logout
                    </button>
                  </div>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  )
}

export default Header